import CompareOfferLineChartTooltip from 'components/tooltip/CompareOfferLineChartTooltip';
import dayjs from 'dayjs';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartOption, CompareOfferLineChartData } from 'types/chart';
import { formatDanjiAveragePriceTick } from 'utils/formatUtil';

interface Props {
  data: CompareOfferLineChartData[];
  chartKey: string;
  chartOption: ChartOption;
  isVisibleDay: boolean;
  isCompareButton: boolean | undefined;
  newChartType: string | undefined;
  compareTextType: string | undefined;
}

function CompareOfferLineChart({
  data,
  chartKey,
  chartOption,
  isVisibleDay,
  isCompareButton,
  newChartType,
  compareTextType,
}: Props) {
  const { tooltipActive, xAxisProps, yAxisProps } = chartOption;

  const getChartLegendName = (value: string) => {
    const valueType = compareTextType === 'complex_compare';

    if (!valueType && value === 'sellingOfferDanjiAveragePrice') {
      return '우리 집 아파트';
    }

    if (!valueType && value === 'wantBuyingOfferDanjiAveragePrice') {
      return '갈아탈 아파트';
    }

    if (valueType && value === 'sellingOfferDanjiAveragePrice') {
      return '단지 1';
    }

    if (valueType && value === 'wantBuyingOfferDanjiAveragePrice') {
      return '단지 2';
    }

    return '-';
  };

  const renderColorfulLegendText = (value: string, entry: any) => {
    const { color } = entry;
    return (
      <span style={{ color, fontSize: 12, fontWeight: 700 }}>
        {getChartLegendName(value)}
      </span>
    );
  };

  return (
    <ResponsiveContainer
      key={chartKey}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <LineChart data={data}>
        <CartesianGrid vertical={false} stroke="#E6E6E6" />

        <Legend
          verticalAlign="top"
          height={36}
          formatter={renderColorfulLegendText}
        />
        <XAxis
          dataKey="date"
          axisLine={{ stroke: 'none' }}
          padding={{ right: 15, left: 15 }}
          interval={xAxisProps?.interval}
          tick={{
            fill: '#d7d7d7',
            fontSize: '0.6rem',
            fontWeight: 700,
          }}
          tickLine={false}
          tickFormatter={(value) => {
            const yearMonth = dayjs(value).format('YYYY-MM');
            const monthDay = dayjs(value).format(`MM-DD`);
            const index = data.findIndex((data) => data.date === value); // 현재 데이터의 인덱스를 가져옴
            // 이전 데이터의 년도를 가져옴
            const prevYear =
              index > 0 ? dayjs(data[index - 1].date).format('YYYY') : '';

            if (isVisibleDay) return monthDay;
            // 현재 데이터의 년도와 이전 데이터의 년도가 다를 경우에만 년도를 표시
            return yearMonth !== prevYear ? yearMonth : '';
          }}
        />
        <YAxis
          type="number"
          tickCount={yAxisProps?.tickCount}
          ticks={yAxisProps?.ticks}
          domain={yAxisProps?.domain}
          interval={0}
          width={45}
          axisLine={false}
          tickLine={false}
          tick={{
            fill: '#d7d7d7',
            fontSize: '0.6rem',
            fontWeight: 700,
          }}
          tickFormatter={(value) => formatDanjiAveragePriceTick(value)}
        />

        <Tooltip
          filterNull
          content={
            <CompareOfferLineChartTooltip
              isVisibleDay={isVisibleDay}
              isCompareButton={isCompareButton}
              newChartType={newChartType}
              compareTextType={compareTextType}
            />
          }
        />

        <Line
          dataKey="sellingOfferDanjiAveragePrice"
          dot={false}
          connectNulls
          type="monotone"
          stroke="#F77933"
          strokeWidth={3}
          isAnimationActive={false}
        />
        <Line
          dataKey="wantBuyingOfferDanjiAveragePrice"
          dot={false}
          connectNulls
          type="monotone"
          stroke="#4584FF"
          strokeWidth={3}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default CompareOfferLineChart;
